import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './Toast';

const STEPS_INPI_GUIDE = [
  {
    id: 1,
    titre: '1. Forme Juridique & Identité',
    inpi_section: 'Section : Identité de l\'entreprise',
    champs: [
      { label: 'Forme Juridique', key: 'statut_juridique', desc: 'Sélectionnez SASU, EURL ou Micro-entreprise' },
      { label: 'Dénomination Sociale', key: 'denommination_sociale', desc: 'Nom officiel de votre société' },
      { label: 'Capital Social (€)', key: 'capital_social', desc: 'Montant total libéré à la banque' }
    ],
    conseil: '💡 Indiquez le nom exact qui figure à l\'Article 3 de vos Statuts Constitutifs.'
  },
  {
    id: 2,
    titre: '2. Siège Social & Domiciliation',
    inpi_section: 'Section : Adresse de l\'établissement principal',
    champs: [
      { label: 'Adresse du Siège Social', key: 'adresse_siege', desc: 'Lieu de direction effective' },
      { label: 'Nom du Dirigeant / Gérant', key: 'nom_president_gerant', desc: 'Représentant légal nommé' }
    ],
    conseil: '💡 Téléversez l\'attestation 03_Attestation_Domiciliation_Siege.pdf générée par ComptaAI.'
  },
  {
    id: 3,
    titre: '3. Activité Principale & Code NAF/APE',
    inpi_section: 'Section : Description des activités',
    champs: [
      { label: 'Objet Social (Activité)', key: 'objet_social', desc: 'Description complète des prestations' }
    ],
    conseil: '💡 Cochez "Prestation de services" ou "Commerciale" pour obtenir le bon code NAF auprès de l\'INSEE.'
  },
  {
    id: 4,
    titre: '4. Options Fiscables & TVA',
    inpi_section: 'Section : Régime d\'imposition',
    champs: [
      { label: 'Régime d\'imposition des bénéfices', key: 'regime_fiscal', desc: 'Impôt sur les Sociétés (IS) recommandé en SASU' },
      { label: 'Régime de TVA', key: 'regime_tva', desc: 'Franchise en base (Art. 293B) ou Réel Simplifié' }
    ],
    conseil: '💡 Si vous êtes en SASU avec l\'ARE France Travail, optez pour l\'IS (Impôt sur les Sociétés).'
  },
  {
    id: 5,
    titre: '5. Téléversement des Pièces (PDF)',
    inpi_section: 'Section : Pièces justificatives',
    champs: [],
    conseil: '💡 Glissez-déposez les PDF contenus dans le PACK_CREATION_INPI.zip téléchargé ci-dessous.'
  },
  {
    id: 6,
    titre: '6. Validation FranceConnect+',
    inpi_section: 'Section : Signature électronique',
    champs: [],
    conseil: '💡 Connectez-vous avec FranceConnect+ (Identité Numérique La Poste) pour signer électroniquement.'
  }
];

const CopiloteCreationEntreprise = ({ clientId }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cote_a_cote'); // 'cote_a_cote' | 'simulateur' | 'pack'
  const [activeStepInpi, setActiveStepInpi] = useState(1);
  const [generatingPack, setGeneratingPack] = useState(false);
  const [inputQuestion, setInputQuestion] = useState('');
  const [chatting, setChatting] = useState(false);

  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'bot',
      text: 'Bonjour ! Je suis votre Copilote Côte-à-Côte INPI. Ouvrez procedures.inpi.fr d\'un côté, et suivez mes recommandations champ par champ ici !'
    }
  ]);

  const [statutForm, setStatutForm] = useState({
    statut_juridique: 'sasu',
    denommination_sociale: '',
    capital_social: 1000,
    nom_president_gerant: '',
    adresse_siege: '10 Rue de la Paix, 75002 Paris',
    objet_social: 'Prestations de conseil, services informatiques et développement logiciel.',
    date_naissance_dirigeant: '01/01/1990',
    ville_naissance_dirigeant: 'Paris',
    nom_pere: 'Dupont Pierre',
    nom_mere: 'Martin Marie'
  });

  const [progression, setProgression] = useState({
    statut_juridique: 'sasu',
    nom_entreprise: '',
    activite_principale: '',
    ca_previsionnel: 60000.0,
    situation_france_travail: 'are',
    etapes: {
      etape_1_cadrage: false,
      etape_2_statuts: false,
      etape_3_capital: false,
      etape_4_annonce_legale: false,
      etape_5_inpi: false,
      etape_6_lancement: false
    }
  });

  const [simuInput, setSimuInput] = useState({
    ca_annuel: 60000,
    achats_charges_annuelles: 10000,
    situation_chomage: 'are',
    allocation_mensuelle_are: 1500
  });

  const calculateSimulationLocal = (inputData) => {
    const ca = parseFloat(inputData?.ca_annuel || 0);
    const charges = parseFloat(inputData?.achats_charges_annuelles || 0);
    const situation = inputData?.situation_chomage || 'are';
    const areMensuelle = parseFloat(inputData?.allocation_mensuelle_are || 0);

    const benefice = Math.max(0, ca - charges);

    // 1. Micro-Entreprise (Services: 21.1% cotisations, 50% abattement IR)
    const cotisMicro = ca * 0.211;
    const revenuImposableMicro = ca * 0.50;
    const impotMicro = revenuImposableMicro > 11000 ? Math.max(0, (revenuImposableMicro - 11000) * 0.11) : 0;
    const netMicro = Math.max(0, ca - cotisMicro - charges - impotMicro);

    // 2. SASU (IS + Dividendes sans salaire -> Maintien ARE 100%)
    const isSasu = (Math.min(benefice, 42500) * 0.15) + (Math.max(0, benefice - 42500) * 0.25);
    const dividendesBruts = Math.max(0, benefice - isSasu);
    const flatTaxSasu = dividendesBruts * 0.30;
    const netSasuDividendes = Math.max(0, dividendesBruts - flatTaxSasu);
    const areAnnuel = situation === 'are' ? (areMensuelle * 12) : 0;
    const netTotalSasuAvecAre = netSasuDividendes + areAnnuel;

    // 3. EURL (Gérant TNS -> ~45% cotisations sur bénéfice)
    const remunBruteEurl = benefice / 1.45;
    const cotisEurl = remunBruteEurl * 0.45;
    const impotEurl = remunBruteEurl > 11000 ? Math.max(0, (remunBruteEurl - 11000) * 0.11) : 0;
    const netEurl = Math.max(0, remunBruteEurl - impotEurl);

    return {
      success: true,
      ca_annuel: ca,
      benefice_brut: benefice,
      comparatif: [
        {
          statut: "Micro-Entreprise",
          label: "Micro-Entreprise (Franchise de TVA & Plafond 77.7k€)",
          cotisations_sociales: Math.round(cotisMicro),
          impots: Math.round(impotMicro),
          net_disponible: Math.round(netMicro),
          avancement: "Idéal pour démarrer rapidement sans comptabilité complexe."
        },
        {
          statut: "SASU",
          label: "SASU (Président / Incompressible avec France Travail ARE)",
          cotisations_sociales: 0,
          impots: Math.round(isSasu + flatTaxSasu),
          net_disponible: Math.round(netSasuDividendes),
          net_avec_are: situation === 'are' ? Math.round(netTotalSasuAvecAre) : null,
          avancement: "Recommandé si vous bénéficiez de l'ARE France Travail !"
        },
        {
          statut: "EURL",
          label: "EURL (Gérant TNS / Cotisations réduites ~45%)",
          cotisations_sociales: Math.round(cotisEurl),
          impots: Math.round(impotEurl),
          net_disponible: Math.round(netEurl),
          avancement: "Le plus avantageux pour se verser un salaire régulier au meilleur coût social."
        }
      ]
    };
  };

  const [simuResult, setSimuResult] = useState(() => calculateSimulationLocal(simuInput));

  const runSimulation = async (inputData) => {
    // Calcul instantané côté client (garantie anti-blocage)
    const localRes = calculateSimulationLocal(inputData);
    setSimuResult(localRes);

    const targetId = clientId || 'projet_defaut';
    try {
      const resp = await api.post(`/copilote-creation/${targetId}/simuler-statut`, inputData);
      if (resp && resp.success && resp.comparatif) {
        setSimuResult(resp);
      }
    } catch (err) {
      console.warn("Calcul API fallback sur calcul local:", err);
    }
  };

  const handleCopyValue = (val, label) => {
    if (!val) {
      addToast(`⚠️ Aucun texte à copier pour ${label}`, 'error');
      return;
    }
    navigator.clipboard.writeText(String(val));
    addToast(`📋 "${label}" copié dans le presse-papier ! Renseignez-le sur l'INPI.`, 'success');
  };

  const handleSendChat = async (qOverride) => {
    const q = qOverride || inputQuestion;
    if (!q.trim()) return;

    const newMsgs = [...chatMessages, { sender: 'user', text: q }];
    setChatMessages(newMsgs);
    if (!qOverride) setInputQuestion('');
    setChatting(true);

    try {
      const resp = await api.post(`/copilote-creation/${clientId}/chat`, {
        question: q,
        statut_en_cours: statutForm.statut_juridique,
        activite: statutForm.objet_social
      });

      if (resp.success) {
        setChatMessages([...newMsgs, { sender: 'bot', text: resp.reponse }]);
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur du copilote IA', 'error');
    } finally {
      setChatting(false);
    }
  };

  const handleGenererPackZip = async (e) => {
    e.preventDefault();
    if (!statutForm.denommination_sociale) {
      addToast('⚠️ Veuillez renseigner le nom de la société', 'error');
      return;
    }

    setGeneratingPack(true);
    try {
      addToast('⏳ Rédaction et assemblage du Pack Dossier INPI (ZIP)...', 'info');
      const response = await fetch(`/copilote-creation/${clientId}/generer-pack-inpi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(statutForm)
      });

      if (!response.ok) throw new Error('Erreur de génération du pack');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PACK_CREATION_INPI_${statutForm.denommination_sociale}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      addToast('✅ Pack Complet Dossier INPI (ZIP) téléchargé !', 'success');
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur lors du téléchargement du Pack INPI', 'error');
    } finally {
      setGeneratingPack(false);
    }
  };

  const formatEuro = (val) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val || 0);
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', background: '#F8FAFC', padding: '24px', minHeight: '100vh', boxSizing: 'border-box' }}>
      
      {/* Header avec bouton d'accès direct INPI */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.5px', margin: 0 }}>
              🚀 Copilote Côte-à-Côte INPI
            </h1>
            <span style={{ background: '#E0F2FE', color: '#0369A1', border: '1px solid #BAE6FD', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
              ✓ Direct Guichet Unique INPI (procedures.inpi.fr)
            </span>
          </div>
          <p style={{ fontSize: '14px', color: '#64748B', marginTop: '4px', margin: '4px 0 0' }}>
            Copiez-collez vos informations en 1-clic et suivez les instructions du Copilote pendant la saisie sur le portail de l'État.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <a
            href="https://procedures.inpi.fr/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '10px 18px', background: 'linear-gradient(135deg, #0284C7, #0369A1)',
              color: 'white', border: 'none', borderRadius: '10px', fontSize: '13.5px',
              fontWeight: '700', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 4px 12px rgba(2, 132, 199, 0.2)'
            }}
          >
            <span>🌐</span> Ouvrir Guichet Unique INPI (procedures.inpi.fr) ↗
          </a>
        </div>
      </div>

      {/* Onglets de Navigation */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #E2E8F0', marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTab('cote_a_cote')}
          style={{
            padding: '10px 18px', background: 'none', border: 'none',
            borderBottom: activeTab === 'cote_a_cote' ? '3px solid #1D9E75' : '3px solid transparent',
            color: activeTab === 'cote_a_cote' ? '#1D9E75' : '#64748B',
            fontWeight: '700', fontSize: '14px', cursor: 'pointer'
          }}
        >
          📱 Copilote Côte-à-Côte INPI (Assist-Fill)
        </button>

        <button
          onClick={() => setActiveTab('pack')}
          style={{
            padding: '10px 18px', background: 'none', border: 'none',
            borderBottom: activeTab === 'pack' ? '3px solid #1D9E75' : '3px solid transparent',
            color: activeTab === 'pack' ? '#1D9E75' : '#64748B',
            fontWeight: '700', fontSize: '14px', cursor: 'pointer'
          }}
        >
          📦 Pack 6 Documents PDF (ZIP)
        </button>

        <button
          onClick={() => setActiveTab('simulateur')}
          style={{
            padding: '10px 18px', background: 'none', border: 'none',
            borderBottom: activeTab === 'simulateur' ? '3px solid #1D9E75' : '3px solid transparent',
            color: activeTab === 'simulateur' ? '#1D9E75' : '#64748B',
            fontWeight: '700', fontSize: '14px', cursor: 'pointer'
          }}
        >
          🧮 Simulateur Statut & France Travail
        </button>
      </div>

      {/* Rendu Onglet 1 : Copilote Côte-à-Côte INPI */}
      {activeTab === 'cote_a_cote' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '24px' }}>
          
          {/* Panneau Gauche : Guide & Cartes Copier/Coller Champ par Champ */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '17px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                📋 Remplissage Pas-à-Pas pour `procedures.inpi.fr`
              </h2>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#0369A1' }}>Étape {activeStepInpi || 1} sur 6</span>
            </div>

            {/* Navigation des 6 Étapes INPI */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '20px' }}>
              {STEPS_INPI_GUIDE.map((st) => (
                <button
                  key={st.id}
                  onClick={() => setActiveStepInpi(st.id)}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '700',
                    background: activeStepInpi === st.id ? '#1D9E75' : '#F1F5F9',
                    color: activeStepInpi === st.id ? 'white' : '#475569',
                    cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  {st.titre}
                </button>
              ))}
            </div>

            {/* Détails de l'étape active */}
            {(() => {
              const currentStep = STEPS_INPI_GUIDE.find(s => s.id === activeStepInpi);
              if (!currentStep) return null;

              return (
                <div>
                  <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#0369A1' }}>{currentStep.inpi_section}</div>
                    <div style={{ fontSize: '12.5px', color: '#334155', marginTop: '4px' }}>{currentStep.conseil}</div>
                  </div>

                  {/* Cartes de saisie avec Bouton "Copier 1-Clic" */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {currentStep.champs.map((ch, idx) => {
                      const val = statutForm[ch.key] || progression[ch.key] || '';
                      return (
                        <div key={idx} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase' }}>{ch.label}</div>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', marginTop: '2px' }}>
                              {val || <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>Non renseigné</span>}
                            </div>
                            <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>{ch.desc}</div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleCopyValue(val, ch.label)}
                            style={{
                              padding: '8px 14px', background: '#E1F5EE', color: '#0F6E56',
                              border: '1px solid #A7F3D0', borderRadius: '8px', fontSize: '12px',
                              fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                          >
                            <span>📋</span> Copier
                          </button>
                        </div>
                      );
                    })}

                    {currentStep.champs.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: '#64748B', fontSize: '13px' }}>
                        Téléversez directement les documents PDF contenus dans le <strong>PACK_CREATION_INPI.zip</strong> téléchargé dans l'onglet Pack.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Panneau Droit : Assistance IA & Aide aux Questions INPI */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>🤖</span>
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                  Assistance Directe Saisie INPI
                </h2>
                <span style={{ fontSize: '11px', color: '#64748B' }}>Posez une question sur n'importe quel champ du site de l'INPI</span>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: '300px', maxHeight: '380px', overflowY: 'auto', background: '#F8FAFC', borderRadius: '12px', padding: '12px', marginBottom: '12px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {chatMessages.map((m, idx) => (
                <div key={idx} style={{ alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                  <div style={{
                    padding: '10px 14px', borderRadius: '12px', fontSize: '12.5px', lineHeight: '1.4', whiteSpace: 'pre-wrap',
                    background: m.sender === 'user' ? '#1D9E75' : 'white',
                    color: m.sender === 'user' ? 'white' : '#0F172A',
                    border: m.sender === 'user' ? 'none' : '1px solid #E2E8F0'
                  }}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Ex: Que cocher pour le régime de TVA ?"
                value={inputQuestion}
                onChange={(e) => setInputQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px' }}
              />
              <button
                onClick={() => handleSendChat()}
                disabled={chatting}
                style={{ padding: '10px 16px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}
              >
                Envoyer
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Rendu Onglet 2 : Pack 6 Documents PDF (ZIP) */}
      {activeTab === 'pack' && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px' }}>📦</span>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                Générateur du Pack Complet INPI (6 Documents PDF en ZIP)
              </h2>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '2px 0 0' }}>
                Éditez l'intégralité de vos pièces officielles obligatoires pour le Guichet Unique INPI / RNE en 1 seul clic.
              </p>
            </div>
          </div>

          <form onSubmit={handleGenererPackZip} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Forme Juridique</label>
              <select
                value={statutForm.statut_juridique}
                onChange={(e) => setStatutForm({ ...statutForm, statut_juridique: e.target.value })}
                style={{ width: '100%', padding: '9px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', background: 'white' }}
              >
                <option value="sasu">SASU (Société par Actions Simplifiée Unipersonnelle)</option>
                <option value="eurl">EURL (Entreprise Unipersonnelle à Responsabilité Limitée)</option>
                <option value="sas">SAS (Société par Actions Simplifiée Multi-associés)</option>
                <option value="sarl">SARL (Société à Responsabilité Limitée Multi-associés)</option>
                <option value="micro">EI / Micro-Entreprise</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Dénomination Sociale (Nom) *</label>
              <input
                type="text"
                placeholder="Ex: NOVA CONSULTING SASU"
                value={statutForm.denommination_sociale}
                onChange={(e) => setStatutForm({ ...statutForm, denommination_sociale: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Capital Social (€)</label>
              <input
                type="number"
                placeholder="1000"
                value={statutForm.capital_social}
                onChange={(e) => setStatutForm({ ...statutForm, capital_social: parseFloat(e.target.value) || 0 })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Nom & Prénom du Dirigeant *</label>
              <input
                type="text"
                placeholder="Ex: Jean Dupont"
                value={statutForm.nom_president_gerant}
                onChange={(e) => setStatutForm({ ...statutForm, nom_president_gerant: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Adresse du Siège Social *</label>
              <input
                type="text"
                placeholder="Ex: 10 Rue de la Paix, 75002 Paris"
                value={statutForm.adresse_siege}
                onChange={(e) => setStatutForm({ ...statutForm, adresse_siege: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Objet Social (Activité)</label>
              <input
                type="text"
                placeholder="Conseil, informatique, vente..."
                value={statutForm.objet_social}
                onChange={(e) => setStatutForm({ ...statutForm, objet_social: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div style={{ gridColumn: 'span 3', display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button
                type="submit"
                disabled={generatingPack}
                style={{
                  padding: '12px 26px', background: 'linear-gradient(135deg, #0284C7, #0369A1)',
                  color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px',
                  fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)'
                }}
              >
                <span>📦</span> {generatingPack ? 'Assemblage du ZIP...' : 'GÉNÉRER LE PACK DOSSIER INPI COMPLET (ZIP)'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rendu Onglet 3 : Simulateur Comparatif & France Travail */}
      {activeTab === 'simulateur' && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <span style={{ fontSize: '24px' }}>🧮</span>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                Simulateur Comparatif de Statut & Droits France Travail (ARE / ARCE)
              </h2>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '2px 0 0' }}>
                Ajustez vos paramètres prévisionnels pour calculer votre rémunération nette et l'optimisation de vos allocations chômage.
              </p>
            </div>
          </div>

          {/* Formulaire de Saisie des Paramètres de Simulation */}
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              📊 Vos Paramètres Prévisionnels :
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                  Chiffre d'Affaires annuel (€)
                </label>
                <input
                  type="number"
                  value={simuInput.ca_annuel}
                  onChange={(e) => setSimuInput({ ...simuInput, ca_annuel: parseFloat(e.target.value) || 0 })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                  Achats & Charges annuelles (€)
                </label>
                <input
                  type="number"
                  value={simuInput.achats_charges_annuelles}
                  onChange={(e) => setSimuInput({ ...simuInput, achats_charges_annuelles: parseFloat(e.target.value) || 0 })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                  Option France Travail
                </label>
                <select
                  value={simuInput.situation_chomage}
                  onChange={(e) => setSimuInput({ ...simuInput, situation_chomage: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13.5px', fontWeight: '600', background: 'white' }}
                >
                  <option value="are">Maintien ARE (Allocation Mensuelle)</option>
                  <option value="arce">Option ARCE (Capital 60% en 2 fois)</option>
                  <option value="aucune">Aucune indemnisation / Salarié</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                  Allocation ARE mensuelle (€/mois)
                </label>
                <input
                  type="number"
                  value={simuInput.allocation_mensuelle_are}
                  onChange={(e) => setSimuInput({ ...simuInput, allocation_mensuelle_are: parseFloat(e.target.value) || 0 })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '14px', fontWeight: '600', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <button
              onClick={() => runSimulation(simuInput)}
              style={{
                marginTop: '16px', padding: '12px 24px', background: 'linear-gradient(135deg, #1D9E75, #10B981)',
                color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700',
                cursor: 'pointer', boxShadow: '0 4px 12px rgba(29, 158, 117, 0.2)', display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <span>🧮</span> Recalculer la Simulation de Revenus
            </button>
          </div>

          {/* Affichage des Cartes de Résultats Comparatifs */}
          {simuResult && simuResult.comparatif ? (
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', marginBottom: '16px' }}>
                🏆 Résultat du Comparatif Financier & Fiscal :
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {simuResult.comparatif.map((c, idx) => {
                  const isRecommended = c.statut === 'SASU';
                  return (
                    <div
                      key={idx}
                      style={{
                        background: isRecommended ? '#F0FDF4' : '#FFFFFF',
                        border: isRecommended ? '2px solid #10B981' : '1px solid #E2E8F0',
                        borderRadius: '14px', padding: '20px', position: 'relative',
                        boxShadow: isRecommended ? '0 8px 24px rgba(16, 185, 129, 0.12)' : 'none'
                      }}
                    >
                      {isRecommended && (
                        <span style={{
                          position: 'absolute', top: '-12px', right: '16px',
                          background: '#10B981', color: 'white', fontSize: '11px',
                          fontWeight: '800', padding: '4px 12px', borderRadius: '12px',
                          textTransform: 'uppercase'
                        }}>
                          ⭐ Option Recommandée
                        </span>
                      )}

                      <div style={{ fontSize: '16px', fontWeight: '800', color: '#0F172A', marginBottom: '8px' }}>
                        {c.label}
                      </div>

                      <div style={{ fontSize: '13px', color: '#475569', marginBottom: '14px', lineHeight: '1.4' }}>
                        {c.avancement}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #E2E8F0', paddingTop: '12px', fontSize: '13px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748B' }}>Cotisations Sociales :</span>
                          <span style={{ fontWeight: '600', color: '#0F172A' }}>{formatEuro(c.cotisations_sociales)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748B' }}>Impôts (IS + IR / Flat Tax) :</span>
                          <span style={{ fontWeight: '600', color: '#0F172A' }}>{formatEuro(c.impots)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #CBD5E1', paddingTop: '6px' }}>
                          <span style={{ color: '#64748B' }}>Net disponible société :</span>
                          <span style={{ fontWeight: '700', color: '#1D9E75' }}>{formatEuro(c.net_disponible)}</span>
                        </div>

                        {c.net_avec_are && (
                          <div style={{
                            background: '#E0F2FE', border: '1px solid #BAE6FD',
                            padding: '8px 12px', borderRadius: '8px', marginTop: '6px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                          }}>
                            <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#0369A1' }}>🎉 Cumul Revenu + ARE France Travail :</span>
                            <span style={{ fontSize: '14px', fontWeight: '800', color: '#0284C7' }}>{formatEuro(c.net_avec_are)} / an</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Information ARCE complémentaire */}
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '16px', marginTop: '20px' }}>
                <div style={{ fontWeight: '700', color: '#1E40AF', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>💰</span> Option ARCE (Capital 60% versé par France Travail)
                </div>
                <p style={{ fontSize: '13px', color: '#1E3A8A', margin: '6px 0 0', lineHeight: '1.4' }}>
                  Si vous optez pour l'ARCE au lieu du maintien mensuel de l'ARE, vous recevrez un capital total estimé à <strong>{formatEuro((simuInput.allocation_mensuelle_are * 24.0) * 0.60)}</strong> versé en 2 tranches égales (30% à l'immatriculation et 30% après 6 mois d'activité).
                </p>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }}></div>
              <div style={{ color: '#64748B', fontSize: '14px' }}>Calcul de votre simulation en cours...</div>
            </div>
          )}

        </div>
      )}

    </div>
  );
};

export default CopiloteCreationEntreprise;
